import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { Link, Paper } from '@material-ui/core';
import {
    Button,
    CheckboxField,
    Color,
    InputField,
    InputType,
    Size,
    Variant,
} from '@signaturit/react-components-library';

import { RegisterFormHeader } from './RegisterFormHeader/RegisterFormHeader';

import { registerValidation } from './RegisterFormHelper';
import { useRegisterFormStyles } from './RegisterForm.styles';

export interface IRegisterFieldsValues {
    name: string;
    email: string;
    password: string;
}

interface IRegisterFormProps {
    formDisabled: boolean;
    submitHandler: (formValues: IRegisterFieldsValues) => void;
}

export const RegisterForm = ({ formDisabled, submitHandler }: IRegisterFormProps) => {
    const { t } = useTranslation();
    const classes = useRegisterFormStyles();

    const [acceptTermsAndConditions, setAcceptTermsAndConditions] = useState(false);
    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            password: '',
        },
        onSubmit: (values, { validateForm }) => {
            validateForm(values);
        },
        validationSchema: registerValidation,
    });
    const isFormValid = formik.isValid && formik.dirty && acceptTermsAndConditions;
    const canSubmit = isFormValid && !formDisabled;
    const hasNameErrors = !!(formik.errors && formik.touched.name && formik.errors.name);
    const hasEmailErrors = !!(formik.errors && formik.touched.email && formik.errors.email);
    const hasPasswordErrors = !!(formik.errors && formik.touched.password && formik.errors.password);
    const { values } = formik;

    const submit = useCallback(() => {
        if (canSubmit) {
            const formValues: IRegisterFieldsValues = {
                name: values.name,
                email: values.email,
                password: values.password,
            };
            submitHandler(formValues);
        }
    }, [submitHandler, values, canSubmit]);

    return (
        <Paper data-signa="auth-register-form-container">
            <RegisterFormHeader />
            <InputField
                label={t('auth.register_form.name_surname')}
                placeholder={t('auth.register_form.name_place_holder')}
                name="name"
                validation={{
                    isError: hasNameErrors,
                    message: (formik.errors.name as string) || '',
                }}
                required={true}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={formDisabled}
            />

            <InputField
                label={t('auth.register_form.company_email')}
                placeholder={t('auth.register_form.company_email_placeholder')}
                name="email"
                validation={{
                    isError: hasEmailErrors,
                    message: (formik.errors.email as string) || '',
                }}
                required={true}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
            />

            <InputField
                label={t('auth.register_form.password')}
                placeholder={t('auth.register_form.password_placeholder')}
                type={InputType.Password}
                name="password"
                validation={{
                    isError: hasPasswordErrors,
                    message: (formik.errors.password as string) || '',
                }}
                required={true}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={formDisabled}
            />
            <div className={classes.row}>
                <CheckboxField
                    name="terms-and-conditions"
                    checked={acceptTermsAndConditions}
                    label={
                        <div className={classes.checkbox}>
                            <Trans
                                className={classes.checkbox}
                                i18nKey={'auth.register_form.i_accept_all_terms_and_conditions'}
                                components={[
                                    <Link href={t('auth.register_form.privacy_link')} />,
                                    <Link href={t('auth.register_form.terms_and_conditions')} />,
                                ]}
                            />
                        </div>
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setAcceptTermsAndConditions(event.target.checked)
                    }
                    disabled={formDisabled}
                />
            </div>

            <Button
                name="auth-register-form-submit"
                disabled={!canSubmit}
                variant={Variant.contained}
                color={Color.primary}
                onClick={submit}
                size={Size.fullWidth}
            >
                {t('auth.register_form.sign_up_now')}
            </Button>
        </Paper>
    );
};
